import React, { useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Points, PointMaterial } from "@react-three/drei";
import * as THREE from "three";

interface StarFieldProps {
  count?: number;
}

const StarField: React.FC<StarFieldProps> = ({ count = 1500 }) => {
  const ref = React.useRef<THREE.Points>(null);

  const [positions, colors] = useMemo(() => {
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const colorPalette = [
      [1.0, 1.0, 1.0], // White
      [0.2, 0.5, 1.0], // Bright blue
      [1.0, 0.4, 0.4], // Bright red
      [1.0, 0.8, 0.2], // Bright yellow
      [0.4, 1.0, 0.4], // Bright green
      [1.0, 0.4, 1.0], // Bright purple
      [0.0, 1.0, 1.0], // Cyan
      [1.0, 0.6, 0.0], // Orange
    ];

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      positions[i3] = (Math.random() - 0.5) * 4;
      positions[i3 + 1] = (Math.random() - 0.5) * 4;
      positions[i3 + 2] = (Math.random() - 0.5) * 4;

      // More vibrant color distribution
      const color =
        colorPalette[Math.floor(Math.random() * colorPalette.length)];
      const brightness = 0.7 + Math.random() * 0.3; // Brighter base brightness
      colors[i3] = color[0] * brightness;
      colors[i3 + 1] = color[1] * brightness;
      colors[i3 + 2] = color[2] * brightness;
    }
    return [positions, colors];
  }, [count]);

  useFrame((_state, delta) => {
    if (ref.current) {
      ref.current.rotation.x -= delta / 200;
      ref.current.rotation.y -= delta / 250;
    }
  });

  return (
    <group rotation={[0, 0, Math.PI / 4]}>
      <Points
        ref={ref}
        positions={positions}
        colors={colors}
        stride={3}
        frustumCulled={false}
      >
        <PointMaterial
          transparent
          vertexColors={true}
          size={0.012}
          sizeAttenuation={true}
          depthWrite={false}
          opacity={0.8}
          toneMapped={false}
          blending={THREE.AdditiveBlending}
        />
      </Points>
    </group>
  );
};

const SpaceBackground: React.FC = () => {
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: -1,
        background: "linear-gradient(to bottom, #0a0a1f 0%, #000000 100%)",
      }}
      aria-hidden="true"
    >
      <Canvas
        camera={{ position: [0, 0, 1] }}
        gl={{
          antialias: true,
          alpha: true,
          logarithmicDepthBuffer: true,
        }}
      >
        <StarField />
      </Canvas>
    </div>
  );
};

export default SpaceBackground;
